import { type SocialAssetWithProject } from '../../../shared/types';
import { formatDayMonth } from '../../lib/calendar';
import { TYPE_LABELS, assetChannels, channelLabel, restrictionSource, type SocialActions } from '../../lib/socialCalendar';
import { StatusPill } from './shared';

// ── Library ──────────────────────────────────────────────────────────────

/** The row's own action, right-aligned under the status pill (mockup §10.1
    A). Scheduled rows show the plan itself — "IG · 14 Sep" — instead of a
    button; everything else keeps the desktop verb. */
function RowAction({ asset, actions }: { asset: SocialAssetWithProject; actions: SocialActions }) {
  if (asset.effective_restricted) {
    return restrictionSource(asset) === 'project' ? (
      <span className="social-m-act plain">view note</span>
    ) : (
      <button type="button" className="social-m-act" onClick={() => actions.onUnrestrict(asset)}>
        unrestrict
      </button>
    );
  }
  switch (asset.status) {
    case 'ready':
      return (
        <button type="button" className="social-m-act" onClick={() => actions.onStartSchedule(asset)}>
          schedule
        </button>
      );
    case 'needs_prep':
      return (
        <button type="button" className="social-m-act" onClick={() => actions.onMarkReady(asset)}>
          mark ready
        </button>
      );
    case 'scheduled':
      return (
        <span className="social-m-act plain">
          {channelLabel(assetChannels(asset.channels))} · {formatDayMonth(asset.planned_date)}
        </span>
      );
    default:
      return (
        <button type="button" className="social-m-act" onClick={() => actions.onRepurpose(asset)}>
          repurpose
        </button>
      );
  }
}

export function LibraryRow({
  asset,
  actions,
  onOpen,
}: {
  asset: SocialAssetWithProject;
  actions: SocialActions;
  onOpen: () => void;
}) {
  return (
    <div className="social-m-row">
      <button type="button" className="social-m-open" onClick={onOpen}>
        <span className={`social-m-ttl${asset.status === 'used' ? ' used' : ''}`}>{asset.label}</span>
        <span className="social-m-meta">
          <span className="pill social-type">{TYPE_LABELS[asset.type]}</span>
          <span className="social-m-sep">·</span>
          <span className="social-m-proj">{asset.project_name ?? 'standalone'}</span>
          <span className="social-m-sep">·</span>
          {asset.brand}
        </span>
      </button>
      <span className="social-m-right">
        <StatusPill asset={asset} />
        <RowAction asset={asset} actions={actions} />
      </span>
    </div>
  );
}
